import { omit } from "lodash";
import { DateTime } from "luxon";
import React, { FC } from "react";

import { Card, CardContent, Skeleton } from "@mui/material";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import {
  AllSeriesType,
  AxisConfig,
  ChartContainer,
  ChartsGrid,
  ChartsLegend,
  ChartsTooltip,
  ChartsXAxis,
  ChartsXAxisProps,
  ChartsYAxis,
  LinePlot,
} from "@mui/x-charts";

import useTimePeriod from "../../../hooks/use-time-period";
import { useSelector } from "../../../store";
import {
  GetTimeSeriesApiArg,
  useGetTimeSeriesQuery,
} from "../../../store/pomeriumApi";

const RequestDuration: FC = () => {
  const { period } = useTimePeriod();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const { routeId } = useSelector((state) => state.metrics);
  const now = DateTime.now().startOf("minute");
  const start = now.minus(period);

  let params: GetTimeSeriesApiArg = {
    organizationId,
    clusterId,
    startTime: start.toISO(),
    endTime: now.toISO(),
    routeId: routeId,
    metricId: "upstream_requests",
  };
  if (!routeId) {
    params = omit(params, ["routeId"]);
  }

  const { data: requestPerSecond } = useGetTimeSeriesQuery(params, {
    skip: !organizationId || !clusterId,
  });

  const transformed = requestPerSecond?.map((ts) => ({
    series: {
      id: ts.labels.response_code_class + "requests-per-second",
      label: ts.labels.response_code_class + "00s",
      type: "line",
      data: ts.points.map((p) => p.value),
    } as AllSeriesType<"line">,
    xAxis: {
      id: "requests-per-second-x",
      data: ts.points.map((p) => new Date(p.timestamp)),
      scaleType: "utc",
      min: start.toJSDate(),
      max: now.toJSDate(),
    } as AxisConfig<"utc", any, ChartsXAxisProps>,
  }));

  return (
    <Card
      sx={{
        width: "100%",
        m: 2,
      }}
    >
      <CardContent sx={{ height: "40vh", m: 2 }}>
        <Typography variant={"h4"} color="text.primary" gutterBottom>
          Requests Per Second
        </Typography>
        {!requestPerSecond && (
          <Skeleton width="100%" height="100%" sx={{ transform: "unset" }} />
        )}
        {requestPerSecond && !requestPerSecond?.length && (
          <Alert severity="info">
            No data to display. Data is updated hourly.
          </Alert>
        )}
        {transformed && !!requestPerSecond?.length && (
          <ChartContainer
            xAxis={transformed.map((d) => d.xAxis)}
            series={transformed.map((d) => d.series)}
          >
            <ChartsLegend direction="horizontal" />
            <LinePlot />
            <ChartsXAxis axisId={"requests-per-second-x"} />
            <ChartsYAxis />
            <ChartsGrid horizontal />
            <ChartsTooltip trigger="axis" />
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default RequestDuration;
